<template>
  <BottomSheet
    ref="sheet"
    close-position="end"
    title="Schedule Color Codes and Icons"
    data-cy="schedule-legend-sheet"
    @hide-sheet="emit('close')"
  >
    <ul class="divide-y divide-gray-100">
      <li
        v-for="row in rows"
        :key="row.key"
        class="flex items-start gap-4 px-4 py-3"
        :data-cy="`schedule-legend-row-${row.key}`"
      >
        <ScheduleLegendSwatch
          :row="row"
          :color="row.color"
          :background-color="row.backgroundColor"
        />
        <div class="min-w-0">
          <div class="text-sm text-gray-900">
            <span class="font-semibold">{{ row.label }}</span>
            <span v-if="row.subLabel" class="ml-1">{{ row.subLabel }}</span>
          </div>
          <div class="text-xs text-gray-500">{{ row.description }}</div>
        </div>
      </li>
    </ul>
  </BottomSheet>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import BottomSheet from "@/components/Seranova/BottomSheet/BottomSheet.vue";
import { useScheduleColors } from "@/SchedulerV2/useScheduleColors";
import { ScheduleLegendRow, scheduleLegendRows } from "./scheduleLegendRows";
import ScheduleLegendSwatch from "./ScheduleLegendSwatch.vue";

// The schedule key as a bottom sheet: what each colour and shape on the board
// means. Rows and their wording come from scheduleLegendRows; every colour
// comes from useScheduleColors, the same source the timeline paints with, so
// the key describes the board rather than a second opinion of it.
const { getBGColor, getStatusColor } = useScheduleColors();
const sheet = ref<InstanceType<typeof BottomSheet> | null>(null);

const emit = defineEmits<{
  (e: "close"): void;
}>();

// Resolved once per customizations change rather than per render: getBGColor
// lightens in a loop until the shade is dark enough, so re-deriving it inline
// would rerun that for every dashed row on every re-render.
//
// Icon rows mark a glyph drawn on a card, not a colour behind it, so they never
// ask the board for one. A status with no answer yet -- the tenant's
// customizations have not landed -- yields undefined, and the swatch omits the
// fill rather than painting something that is not the board's colour.
const rows = computed(() =>
  scheduleLegendRows().map((row: ScheduleLegendRow) => {
    const color = row.swatch === "icon" ? undefined : getStatusColor(row.key);
    return {
      ...row,
      color,
      backgroundColor:
        row.swatch === "dashed" && color ? getBGColor(row.key) : undefined
    };
  })
);

defineExpose({ close: () => sheet.value?.close() });
</script>
